import { generateText, parsePartialJson, streamText } from "ai";
import { createOpenAI } from "@ai-sdk/openai";
import { createGoogleGenerativeAI } from "@ai-sdk/google";
import { z } from "zod";
import { getDefaultProvider, getProviderApiKey, type ProviderType } from "@/ai/providers";

type AIMsgPart =
  | { type: "text"; text: string }
  | { type: "image"; image: string }
  | { type: "file"; data: string; mediaType?: string; filename?: string };

type AIMessage = {
  role: "user" | "assistant" | "system";
  content: string | AIMsgPart[];
};

type ClientTool = {
  description: string;
  inputSchema: z.ZodTypeAny;
};

interface GenerateAITextOptions {
  messages: AIMessage[];
  model: string;
  provider?: ProviderType;
  system?: string;
  temperature?: number;
  maxOutputTokens?: number;
}

interface StreamAITextOptions {
  messages: AIMessage[];
  model: string;
  provider?: ProviderType;
  system?: string;
  temperature?: number;
  maxOutputTokens?: number;
  tools?: Record<string, ClientTool>;
  onChunk?: (delta: string) => void;
  /** Called with the complete tool input once all arguments have arrived. */
  onToolCall?: (toolName: string, input: Record<string, unknown>) => Promise<void> | void;
  /**
   * Called on each raw JSON fragment as tool arguments stream in.
   * `accumulated` is the full JSON string so far — use `parsePartialJson` to
   * extract fields progressively.
   */
  onToolInputDelta?: (toolName: string, accumulated: string) => Promise<void> | void;
}

function buildProviderFactory(selectedProvider: ProviderType, apiKey: string) {
  return selectedProvider === "openai"
    ? createOpenAI({ apiKey })
    : createGoogleGenerativeAI({ apiKey });
}

function getApiKey(selectedProvider: ProviderType): string {
  const apiKey = getProviderApiKey(selectedProvider);
  if (!apiKey) {
    throw new Error(
      `${selectedProvider === "openai" ? "OpenAI" : "Gemini"} API key is required. ` +
        "Please configure it in Settings.",
    );
  }
  return apiKey;
}

/**
 * Runs AI text generation entirely client-side (serverless).
 * Uses the user's API key from localStorage (BYOK) and calls OpenAI/Gemini directly from the browser.
 * Use for non-streaming tasks: quiz, translation, summaries.
 */
export async function generateAIText({
  messages,
  model,
  provider,
  system,
  temperature,
  maxOutputTokens,
}: GenerateAITextOptions): Promise<string> {
  const selectedProvider = provider ?? getDefaultProvider();
  const apiKey = getApiKey(selectedProvider);
  const providerFactory = buildProviderFactory(selectedProvider, apiKey);

  const { text } = await generateText({
    model: providerFactory(model),
    system,
    messages,
    temperature: temperature ?? 0.2,
    maxOutputTokens: maxOutputTokens ?? 4000,
  });

  if (!text || typeof text !== "string") {
    throw new Error("AI response is empty");
  }

  return text;
}

/**
 * Streams AI text generation client-side (BYOK).
 * Supports tool calling — tools without `execute` are handled via `onToolCall`.
 * Use `onToolInputDelta` to progressively stream tool argument fields (e.g. policy `content`)
 * as the model streams JSON arguments, before the full tool call is assembled.
 */
export async function streamAIText({
  messages,
  model,
  provider,
  system,
  temperature,
  maxOutputTokens,
  tools,
  onChunk,
  onToolCall,
  onToolInputDelta,
}: StreamAITextOptions): Promise<void> {
  const selectedProvider = provider ?? getDefaultProvider();
  const apiKey = getApiKey(selectedProvider);
  const providerFactory = buildProviderFactory(selectedProvider, apiKey);

  // Build tools map for the SDK — client-side tools have no execute function
  const sdkTools: Record<string, { description: string; inputSchema: z.ZodTypeAny }> | undefined =
    tools
      ? Object.fromEntries(
          Object.entries(tools).map(([name, def]) => [
            name,
            { description: def.description, inputSchema: def.inputSchema },
          ]),
        )
      : undefined;

  const sdkResult = streamText({
    model: providerFactory(model),
    system,
    messages,
    temperature: temperature ?? 0.3,
    maxOutputTokens: maxOutputTokens ?? 8000,
    ...(sdkTools ? { tools: sdkTools } : {}),
  });

  // Track accumulated JSON per tool-call id for onToolInputDelta
  const toolNameById: Record<string, string> = {};
  const toolInputById: Record<string, string> = {};

  for await (const part of sdkResult.fullStream) {
    if (part.type === "text-delta") {
      onChunk?.(part.text);
    } else if (part.type === "tool-input-start") {
      toolNameById[part.id] = part.toolName;
      toolInputById[part.id] = "";
    } else if (part.type === "tool-input-delta") {
      toolInputById[part.id] = (toolInputById[part.id] ?? "") + part.delta;
      const toolName = toolNameById[part.id];
      if (toolName && onToolInputDelta) {
        await onToolInputDelta(toolName, toolInputById[part.id]);
      }
    } else if (part.type === "tool-call") {
      await onToolCall?.(part.toolName, part.input as Record<string, unknown>);
    }
  }
}

export { parsePartialJson };
